import { AuthProvider } from '@/context/AuthContext'
import { CartProvider } from '@/context/Cart'
import { ContentProvider } from '@/context/ContentContext'
import { ThemeProvider } from '@/context/ThemeContext'
import { LanguageProvider } from '@/context/LanguageContext'
import { ToastProvider } from '@/context/ToastContext'
import { GetAppProvider } from '@/context/GetAppContext'
import createEmotionCache from '@/createEmotionCache'
import '@/styles/globals.css'
import { CacheProvider, EmotionCache } from '@emotion/react'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'
import * as React from 'react'
import Layout from '../layouts'
import theme from '../styles/theme'
// import VwoScript from '@/components/scripts/vwoScript'
import MazeScript from '@/components/scripts/maze'
import MetaPixelScript from '@/components/scripts/metaPixel'
import GoogleAnalyticsScript from '@/components/scripts/googleAnalytics'
import { captureMetaClickIds, trackMetaEvent } from '@/utils/metaPixel'
import '@aws-amplify/ui-react/styles.css'

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache()

export interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache
}

const MyApp: React.FC<MyAppProps> = (props) => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props
  const router = useRouter()

  React.useEffect(() => {
    if (!router.isReady) return
    captureMetaClickIds()
  }, [router.isReady, router.asPath])

  React.useEffect(() => {
    const handleRouteChangeComplete = (): void => {
      captureMetaClickIds()
      trackMetaEvent('PageView')
    }

    router.events.on('routeChangeComplete', handleRouteChangeComplete)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete)
    }
  }, [router.events])

  React.useEffect(() => {
    // const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches

    // const selectedTheme = localStorage.getItem('theme')
    // if (selectedTheme !== null) {
    //   document.body.classList.add(selectedTheme)
    //   // setTheme(selectedTheme)
    // } else if (prefersDarkMode) {
    //   document.body.classList.add('dark')
    //   // setTheme('dark')
    // } else {
    //   document.body.classList.add('light')
    //   // setTheme('light')
    // }

    const handleKeyDown = (e: KeyboardEvent): void => {
      const el = e.target as HTMLInputElement
      if (el.tagName === 'INPUT' && el.type === 'number' && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault()
      }
    }
    const handleWheel = (e: WheelEvent): void => {
      const el = e.target as HTMLInputElement
      if (el.tagName === 'INPUT' && el.type === 'number') {
        e.preventDefault()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('wheel', handleWheel, { passive: false })
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('wheel', handleWheel)
    }
  }, [])

  return (
    <CacheProvider value={emotionCache}>
      <Head>
        <title>Мобиком Корпораци</title>
        <meta property='fb:pages' content='105701022801307' />
        <meta name='viewport' content='initial-scale=1, width=device-width' />
        <meta
          name='keywords'
          content='Мобиком, Mobicom, гар утас, үүрэн холбоо, интернэт, 3G, 4G, LTE, харилцаа холбоо, роуминг, сансрын холбоо, контент, wi-fi, mongolian mobile, operator, үүрэн холбоо, mobile network, Mongolia broadband, ict, тэмүүлэл, mongolian wireless provider, mongolian communications, mongolian telco, mongolia mobile operator, mongolia broadband, mongolia wimax'
        />
        <meta name='description' content='Монголын анхны үүрэн холбооны оператор' />
        <meta content='https://www.mobicom.mn/images/logo_mobicom.png' property='og:image' />
        <meta
          name='google-site-verification'
          content='CjnjTQqUgLgXWBPJ_ZilEPiWLwJoaH9GSWp9ND907qY'
        />
      </Head>

      <MuiThemeProvider theme={theme}>
        <CssBaseline />

        <LanguageProvider>
          <AuthProvider>
            <ThemeProvider>
              <ToastProvider>
                <ContentProvider>
                  <CartProvider>
                    <GetAppProvider>
                      <Layout>
                        <GoogleAnalyticsScript />
                        <MazeScript />
                        <MetaPixelScript />
                        {/* <Mascot/> */}
                        <Component {...pageProps} />
                      </Layout>
                    </GetAppProvider>
                  </CartProvider>
                </ContentProvider>
              </ToastProvider>
            </ThemeProvider>
          </AuthProvider>
        </LanguageProvider>
      </MuiThemeProvider>
    </CacheProvider>
  )
}

export default MyApp
